import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useInstall } from './useInstall';
import { IosInstallSheet } from './IosInstallSheet';

// Full-screen "you're verified — now install the app" prompt, shown once right after a
// creator confirms their account (peak motivation). Self-guards: if the app is already
// installed or can't be installed here (desktop), it closes immediately — no empty screen.
export function InstallInterstitial({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation('common');
  const { visible, isInApp, copied, showIos, setShowIos, install, canWebShare, openShareSheet } = useInstall();

  useEffect(() => { if (!visible) onClose(); }, [visible, onClose]);
  if (!visible) return null;

  const title = isInApp ? t('install.openInBrowserTitle', { defaultValue: 'Откройте в браузере, чтобы установить' }) : t('install.interTitle', { defaultValue: 'Аккаунт подтверждён!' });
  const body = isInApp ? t('install.openInBrowserBody', { defaultValue: 'В приложении Instagram установка недоступна. Нажмите «⋯» вверху → «Открыть в браузере», потом «Установить».' }) : t('install.interBody', { defaultValue: 'Установите AdaMar на телефон, чтобы не пропускать задания брендов и получать уведомления сразу на экране.' });
  const cta = isInApp ? (copied ? t('install.copied', { defaultValue: 'Ссылка скопирована' }) : t('install.copyLink', { defaultValue: 'Скопировать ссылку' })) : t('install.interCta', { defaultValue: 'Установить приложение' });

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(6,8,13,0.92)', zIndex: 2050, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ width: '100%', maxWidth: 400, background: '#12151f', border: '1px solid rgba(138,92,255,0.4)', borderRadius: 20, padding: 26, textAlign: 'center', color: 'var(--text)' }}>
        <div style={{ width: 66, height: 66, borderRadius: '50%', margin: '0 auto 16px', background: 'rgba(138,92,255,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg width={34} height={34} viewBox="0 0 24 24" fill="none" stroke="#c9b6ff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="6" y="2" width="12" height="20" rx="2.5" /><path d="m9 12 2 2 4-4" />
          </svg>
        </div>
        <div style={{ fontSize: 19, fontWeight: 700, color: '#fff', marginBottom: 8 }}>{title}</div>
        <div style={{ fontSize: 14, color: 'var(--muted)', lineHeight: 1.55, marginBottom: 22 }}>{body}</div>

        <button type="button" onClick={install}
          style={{ width: '100%', background: 'var(--primary)', border: 'none', color: '#fff', borderRadius: 12, padding: '13px', fontSize: 15, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
          {cta}
        </button>
        <button type="button" onClick={onClose}
          style={{ marginTop: 12, background: 'transparent', border: 'none', color: 'var(--dim)', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit', padding: 6 }}>
          {t('install.later', { defaultValue: 'Позже' })}
        </button>
      </div>

      {showIos && <IosInstallSheet onClose={() => setShowIos(false)} canWebShare={canWebShare} openShareSheet={openShareSheet} />}
    </div>,
    document.body,
  );
}
